/**
 * GateEnforcementPage — Compliance · Gate Enforcement (ID-Expiry / LexisNexis).
 *
 * Edits the per-(CustomerType × GateKey) enforcement strategy in
 * dbo.EntityRuleGateEnforcement for the two gates this Person-Modal pass adds:
 * `IDExpiry` and `LexisNexis` (slot-3 D.5 folds both into the existing cite-
 * enforcing table rather than new EntityRule columns). No standalone gate-
 * strategy editor existed in the admin panel, so this is a new surface built
 * on the canonical primitives.
 *
 * Strategy picker = hard-stop / warn-only / deferred-N / officer-override,
 * constrained per gate to the GateKey's AllowedStrategiesJson. Every write
 * carries a legacy-procedure citation (the table's LegacyProcedureCitation is
 * NOT NULL — uncited rows reject at insert), and overriding a seeded default
 * requires a reason. The ID-Expiry section also surfaces idExpirationWarningDays.
 *
 * Wiring: reads via the shipped client.retail.config.{entityRuleGateEnforcements,
 * gateKeys,entityRules} routes; writes via /v3/admin/entity-rule-gate-
 * enforcements/upsert (documented contract C5 — non-fatal "pending" banner if
 * TR hasn't shipped it to this branch).
 *
 * 5 admin-quality bars: wired (read + upsert) · configurable (strategy /
 * deferral / active per subtype × gate) · makes sense (grouped per gate,
 * default + seeded/overridden shown) · modern control (strategy dropdown +
 * bounded deferral days, no free-text strategy) · no-breaking (citation
 * required; override reason required; deferred-N requires days; no delete).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
  type SystemSettingUpsertBody,
} from '@retail-ops/api-client';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { SelectField, NumberField, TextArea, BoolField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';
import {
  PersonModalConfigApi,
  ENFORCEMENT_STRATEGIES,
  errMessage,
  isRouteNotLive,
  type GateEnforcementRow,
  type GateKeyRow,
} from '@/services/personModalConfigApi';

/** The two gates this Person-Modal pass owns. */
const GATE_KEYS = ['IDExpiry', 'LexisNexis'] as const;
const DEFERRED_STRATEGY = 'deferred-N-days-post-acctadd';

interface EntitySubtype {
  customerType: string;
  displayName: string;
}

interface Draft {
  isNew: boolean;
  customerType: string;
  gateKey: string;
  enforcementStrategy: string;
  deferralDays: number | null;
  legacyProcedureCitation: string;
  overrideReason: string;
  isActive: boolean;
  /** The strategy that was seeded as default — drives the "override reason required" rule. */
  seededDefault: boolean;
}

function strategyLabel(value: string): string {
  return ENFORCEMENT_STRATEGIES.find((s) => s.value === value)?.label ?? value;
}

function strategyPill(value: string, deferralDays?: number | null): JSX.Element {
  switch (value) {
    case 'required-hard-stop':
      return <Pill tone="err">hard stop</Pill>;
    case 'warn-only':
      return <Pill tone="warn">warn only</Pill>;
    case DEFERRED_STRATEGY:
      return <Pill tone="info">deferred {deferralDays ?? '?'}d</Pill>;
    case 'officer-override':
      return <Pill tone="muted">officer override</Pill>;
    case 'deferred-conditional':
      return <Pill tone="info">deferred (cond)</Pill>;
    default:
      return <Pill tone="muted">{value}</Pill>;
  }
}

/** Allowed strategy options for a gate = AllowedStrategiesJson ∩ the 4 canonical,
 *  plus the current value if it's outside the 4 (so editing never silently drops it). */
function strategyOptions(
  gate: GateKeyRow | undefined,
  current?: string,
): ReadonlyArray<{ value: string; label: string }> {
  let allowed: string[] = [];
  try {
    const parsed = JSON.parse(gate?.allowedStrategiesJson ?? '[]');
    if (Array.isArray(parsed)) allowed = parsed.map(String);
  } catch {
    /* keep allowed empty → fall through to all 4 */
  }
  const base: Array<{ value: string; label: string }> = ENFORCEMENT_STRATEGIES.filter(
    (s) => allowed.length === 0 || allowed.includes(s.value),
  ).map((s) => ({ value: s.value as string, label: s.label as string }));
  const opts: Array<{ value: string; label: string }> =
    base.length > 0
      ? base
      : ENFORCEMENT_STRATEGIES.map((s) => ({ value: s.value as string, label: s.label as string }));
  if (current && !opts.some((o) => o.value === current)) {
    opts.unshift({ value: current, label: `${current} (current)` });
  }
  return opts;
}

export function GateEnforcementPage(): JSX.Element {
  const client = useApiClient();
  const api = useMemo(() => new PersonModalConfigApi(client), [client]);

  const [gateKeys, setGateKeys] = useState<ReadonlyArray<GateKeyRow> | null>(null);
  const [rows, setRows] = useState<ReadonlyArray<GateEnforcementRow> | null>(null);
  const [subtypes, setSubtypes] = useState<ReadonlyArray<EntitySubtype>>([]);
  const [warnDaysRow, setWarnDaysRow] = useState<{ key: string; value: string | null; dataType: string } | null>(null);
  const [warnDaysDraft, setWarnDaysDraft] = useState<number | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    const ctrl = new AbortController();
    void (async () => {
      setGateKeys(null);
      setRows(null);
      setLoadError(null);
      try {
        const [gks, enf] = await Promise.all([
          api.listGateKeys(ctrl.signal),
          api.listGateEnforcements(ctrl.signal),
        ]);
        if (ctrl.signal.aborted) return;
        setGateKeys(gks.filter((g) => (GATE_KEYS as readonly string[]).includes(g.key)));
        setRows(enf.filter((r) => (GATE_KEYS as readonly string[]).includes(r.gateKey)));
      } catch (e) {
        if (ctrl.signal.aborted) return;
        setGateKeys([]);
        setRows([]);
        setLoadError(errMessage(e));
      }

      // Subtype catalog for the "add override" picker (best-effort).
      try {
        const er = await client.retail.config.entityRules({ signal: ctrl.signal });
        if (!ctrl.signal.aborted && er.success && Array.isArray(er.data)) {
          setSubtypes(
            er.data.map((r) => ({ customerType: r.customerType, displayName: r.displayName })),
          );
        }
      } catch {
        /* add-override still works by typing a customerType is not offered; edit-only fallback */
      }

      // idExpirationWarningDays — surface from SystemSettings if a row exists.
      try {
        const ss = await client.retail.config.systemSettingsRows({ signal: ctrl.signal });
        if (!ctrl.signal.aborted && ss.success && Array.isArray(ss.data)) {
          const all = ss.data as unknown as ReadonlyArray<{
            settingKey: string;
            settingValue: string | null;
            dataType: string;
          }>;
          const found = all.find((r) => /idexpir.*warn|expir.*warning.*day|idexpirationwarningdays/i.test(r.settingKey));
          if (found) {
            setWarnDaysRow({ key: found.settingKey, value: found.settingValue, dataType: found.dataType });
            setWarnDaysDraft(found.settingValue != null ? Number(found.settingValue) : null);
          } else {
            setWarnDaysRow(null);
          }
        }
      } catch {
        /* non-fatal */
      }
    })();
    return () => ctrl.abort();
  }, [api, client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const rowsByGate = useCallback(
    (gateKey: string) => (rows ?? []).filter((r) => r.gateKey === gateKey),
    [rows],
  );

  const startEdit = useCallback((row: GateEnforcementRow) => {
    setSaveError(null);
    setDraft({
      isNew: false,
      customerType: row.customerType,
      gateKey: row.gateKey,
      enforcementStrategy: row.enforcementStrategy,
      deferralDays: row.deferralDays ?? null,
      legacyProcedureCitation: row.legacyProcedureCitation ?? '',
      overrideReason: row.overrideReason ?? '',
      isActive: row.isActive,
      seededDefault: row.seededDefault ?? false,
    });
  }, []);

  const startAdd = useCallback(
    (gateKey: string) => {
      setSaveError(null);
      const gate = gateKeys?.find((g) => g.key === gateKey);
      setDraft({
        isNew: true,
        customerType: '',
        gateKey,
        enforcementStrategy: gate?.defaultStrategy ?? 'required-hard-stop',
        deferralDays: gate?.defaultDeferralDays ?? null,
        legacyProcedureCitation: '',
        overrideReason: '',
        isActive: true,
        seededDefault: false,
      });
    },
    [gateKeys],
  );

  // Subtypes that don't already have a row for the draft's gate (for the Add picker).
  const addableSubtypes = useMemo(() => {
    if (!draft?.isNew) return [];
    const taken = new Set(rowsByGate(draft.gateKey).map((r) => r.customerType));
    return subtypes.filter((s) => !taken.has(s.customerType));
  }, [draft, rowsByGate, subtypes]);

  const handleSave = useCallback(async () => {
    if (!draft) return;
    if (draft.isNew && draft.customerType.trim().length === 0) {
      setSaveError('Pick a customer subtype for the new override.');
      return;
    }
    if (draft.legacyProcedureCitation.trim().length === 0) {
      setSaveError('A legacy-procedure citation is required — the table enforces NOT-NULL citation.');
      return;
    }
    if (draft.enforcementStrategy === DEFERRED_STRATEGY && (draft.deferralDays == null || draft.deferralDays <= 0)) {
      setSaveError('Deferred-N strategy needs a deferral-days value greater than 0.');
      return;
    }
    if (draft.seededDefault && draft.overrideReason.trim().length === 0) {
      setSaveError('Overriding a seeded default requires a reason (surfaces in the admin-history audit log).');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const resp = await api.upsertGateEnforcement({
        customerType: draft.customerType.trim(),
        gateKey: draft.gateKey,
        enforcementStrategy: draft.enforcementStrategy,
        deferralDays: draft.enforcementStrategy === DEFERRED_STRATEGY ? draft.deferralDays : null,
        legacyProcedureCitation: draft.legacyProcedureCitation.trim(),
        overrideReason: draft.overrideReason.trim() || null,
        isActive: draft.isActive,
      });
      if (!resp.success) throw new Error(resp.error ?? 'upsert returned success=false');
      setToast(`Saved ${draft.gateKey} strategy for ${draft.customerType}.`);
      setDraft(null);
      refresh();
    } catch (e) {
      setSaveError(
        isRouteNotLive(e)
          ? 'Write route not live in this branch yet (Slot TR pending). The editor is wired; upsert works once TR ships /v3/admin/entity-rule-gate-enforcements/upsert.'
          : errMessage(e),
      );
    } finally {
      setSaving(false);
    }
  }, [draft, api, refresh]);

  const saveWarnDays = useCallback(async () => {
    if (!warnDaysRow || warnDaysDraft == null) return;
    setActionError(null);
    try {
      const body: SystemSettingUpsertBody = {
        settingKey: warnDaysRow.key,
        settingValue: String(warnDaysDraft),
        dataType: warnDaysRow.dataType || 'int',
      };
      const resp = await client.retail.admin.systemSettings.upsert(ADMIN_AUTH_TOKEN_SENTINEL, body);
      if (!resp.success) throw new Error(resp.error ?? 'upsert returned success=false');
      setToast(`Updated ${warnDaysRow.key} → ${warnDaysDraft}.`);
      refresh();
    } catch (e) {
      setActionError(
        ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
      );
    }
  }, [warnDaysRow, warnDaysDraft, client, refresh]);

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load gate enforcement: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!gateKeys || !rows) return <Spinner label="Loading gate enforcement…" />;

  const missingGateKeys = GATE_KEYS.filter((k) => !gateKeys.some((g) => g.key === k));

  return (
    <>
      {toast && (
        <Banner tone="info">
          {toast} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}
      {actionError && <Banner tone="err">{actionError}</Banner>}

      <Banner tone="info">
        Per-subtype enforcement strategy for the <strong>ID-Expiry</strong> and{' '}
        <strong>LexisNexis</strong> gates. <strong>Hard stop</strong> blocks until satisfied;{' '}
        <strong>warn only</strong> warns without blocking; <strong>deferred-N</strong> tracks a
        deadline N days post-AcctAdd; <strong>officer override</strong> lets an officer code + reason
        bypass. Every change records a legacy-procedure citation (the table rejects uncited rows),
        and overriding a seeded default requires a reason.
      </Banner>

      {missingGateKeys.length > 0 && (
        <Banner tone="warn">
          <strong>Seed pending.</strong> GateKey{missingGateKeys.length > 1 ? 's' : ''}{' '}
          <code>{missingGateKeys.join(', ')}</code> not present in{' '}
          <code>/v3/config/gate-keys</code> yet (Slot TR seeds them with AllowedStrategiesJson +
          citation). Those sections show once the gate keys land.
        </Banner>
      )}

      {GATE_KEYS.map((gateKey) => {
        const gate = gateKeys.find((g) => g.key === gateKey);
        if (!gate) return null;
        const gateRows = rowsByGate(gateKey);
        return (
          <Card
            key={gateKey}
            title={
              <span>
                {gate.displayName || gateKey}{' '}
                <Pill tone="muted">{gate.scope}</Pill>{' '}
                <span className="cell-desc">default: {strategyLabel(gate.defaultStrategy)}</span>
              </span>
            }
            actions={
              <Button
                variant="primary"
                onClick={() => startAdd(gateKey)}
                disabled={addableSubtypesCount(gateRows, subtypes) === 0}
              >
                + Add override
              </Button>
            }
          >
            {gate.description && <p className="page-intro">{gate.description}</p>}

            {gateRows.length === 0 ? (
              <p className="empty">
                No per-subtype overrides — every subtype falls back to the gate default (
                {strategyLabel(gate.defaultStrategy)}). Add an override to diverge for a specific
                subtype.
              </p>
            ) : (
              <div className="data-table__wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Subtype</th>
                      <th>Strategy</th>
                      <th>Citation</th>
                      <th>Source</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gateRows.map((r) => (
                      <tr key={`${r.gateKey}:${r.customerType}`} className={r.isActive ? undefined : 'conflict-row--off'}>
                        <td className="cell-strong">{r.customerType}</td>
                        <td>{strategyPill(r.enforcementStrategy, r.deferralDays)}</td>
                        <td>
                          <span className="cell-mono" title={r.legacyProcedureCitation}>
                            {r.legacyProcedureCitation.length > 42
                              ? `${r.legacyProcedureCitation.slice(0, 42)}…`
                              : r.legacyProcedureCitation || <span className="cell-dim">—</span>}
                          </span>
                        </td>
                        <td>
                          {r.seededDefault ? (
                            <Pill tone="muted">seeded</Pill>
                          ) : (
                            <Pill tone="info">overridden</Pill>
                          )}
                        </td>
                        <td>
                          {r.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>}
                        </td>
                        <td>
                          <Button onClick={() => startEdit(r)}>Edit</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* ID-Expiry warning-days tunable lives under the IDExpiry gate. */}
            {gateKey === 'IDExpiry' && (
              <div className="di-threshold">
                {warnDaysRow ? (
                  <>
                    <NumberField
                      label="ID-expiry warning window (days)"
                      value={warnDaysDraft}
                      onChange={setWarnDaysDraft}
                      min={0}
                      max={365}
                      step={1}
                      hint={`SystemSetting ${warnDaysRow.key}. Within this many days of expiry the Verify tab shows a yellow warning (red once expired). Legacy default 60d (existingCustomer.ts:36).`}
                    />
                    <div className="di-threshold__actions">
                      <Button
                        variant="primary"
                        disabled={
                          warnDaysDraft == null ||
                          warnDaysDraft < 0 ||
                          String(warnDaysDraft) === (warnDaysRow.value ?? '')
                        }
                        onClick={() => void saveWarnDays()}
                      >
                        Save warning window
                      </Button>
                    </div>
                  </>
                ) : (
                  <Banner tone="warn">
                    <strong>idExpirationWarningDays — seed pending.</strong> No SystemSetting for the
                    ID-expiry warning window exists yet; Slot TR wires it to SQL (currently a TS
                    literal default of 60 days, <code>existingCustomer.ts:36</code>). It becomes
                    editable here once that key is seeded.
                  </Banner>
                )}
              </div>
            )}
          </Card>
        );
      })}

      {/* ── Edit / Add modal ─────────────────────────────────────────────── */}
      <ModalShell
        open={draft !== null}
        title={
          draft
            ? draft.isNew
              ? `Add ${draft.gateKey} override`
              : `Edit ${draft.gateKey} — ${draft.customerType}`
            : ''
        }
        onClose={() => {
          if (saving) return;
          setDraft(null);
          setSaveError(null);
        }}
        width={620}
        actions={
          draft && (
            <>
              <Button onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : draft.isNew ? 'Add override' : 'Save changes'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            {draft.isNew ? (
              <SelectField
                label="Customer subtype"
                value={draft.customerType}
                onChange={(v) => setDraft({ ...draft, customerType: v })}
                placeholder={addableSubtypes.length > 0 ? 'Select a subtype…' : 'No subtypes available'}
                options={addableSubtypes.map((s) => ({
                  value: s.customerType,
                  label: `${s.displayName} (${s.customerType})`,
                }))}
                hint="Only subtypes without an existing override for this gate are listed."
              />
            ) : (
              <div className="gate-locked">
                <strong>Subtype × Gate</strong> (composite key — locked):{' '}
                <code>
                  {draft.customerType} / {draft.gateKey}
                </code>
              </div>
            )}

            <SelectField
              label="Enforcement strategy"
              value={draft.enforcementStrategy}
              onChange={(v) => setDraft({ ...draft, enforcementStrategy: v })}
              options={strategyOptions(
                gateKeys.find((g) => g.key === draft.gateKey),
                draft.enforcementStrategy,
              )}
              hint="Constrained to the strategies this gate allows (AllowedStrategiesJson)."
            />

            {draft.enforcementStrategy === DEFERRED_STRATEGY && (
              <NumberField
                label="Deferral days (post-AcctAdd)"
                value={draft.deferralDays}
                onChange={(v) => setDraft({ ...draft, deferralDays: v })}
                min={1}
                max={365}
                step={1}
                required
                hint="The deadline window tracked after the account is added."
              />
            )}

            <TextArea
              label="Legacy-procedure citation"
              value={draft.legacyProcedureCitation}
              onChange={(v) => setDraft({ ...draft, legacyProcedureCitation: v })}
              rows={2}
              placeholder="PCB procedure memo / Legacy Procedures PDF page / Collette confirmation / Aurora finding."
              hint="Required — EntityRuleGateEnforcement.LegacyProcedureCitation is NOT NULL; uncited rows reject."
            />

            <TextArea
              label={`Override reason${draft.seededDefault ? ' (required — overriding a seeded default)' : ' (optional)'}`}
              value={draft.overrideReason}
              onChange={(v) => setDraft({ ...draft, overrideReason: v })}
              rows={2}
              placeholder="Why this subtype diverges from the seeded default."
              hint="Surfaces in the admin-history audit log."
            />

            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
              hint="Inactive rows are ignored at runtime — the gate falls back to its default strategy. Row stays for audit."
            />
          </div>
        )}
      </ModalShell>
    </>
  );
}

function addableSubtypesCount(
  gateRows: ReadonlyArray<GateEnforcementRow>,
  subtypes: ReadonlyArray<EntitySubtype>,
): number {
  if (subtypes.length === 0) return 1; // allow opening Add even if catalog didn't load
  const taken = new Set(gateRows.map((r) => r.customerType));
  return subtypes.filter((s) => !taken.has(s.customerType)).length;
}
